import FundOpportunityChatResponse from 'FundOpportunityChatResponse';
import AIChatMessage from '../../../models/AIChatMessage';
import { FundOpportunityLabels } from '../../Util/Labels';
import { UploadedDocument, UploadProgress } from './types';

export function generateTempId(): string {
  return `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function getUploadFileName(file: File): string {
  return file.webkitRelativePath && file.webkitRelativePath.length > 0
    ? file.webkitRelativePath
    : file.name;
}

export function getUploadedDocumentsStorageKey(tempFundOpportunityId: string): string {
  return `uploadedDocuments-${tempFundOpportunityId}`;
}

export function loadUploadedDocuments(tempFundOpportunityId: string): UploadedDocument[] {
  const storageKey = getUploadedDocumentsStorageKey(tempFundOpportunityId);
  return JSON.parse(localStorage.getItem(storageKey) || '[]') as UploadedDocument[];
}

export function saveUploadedDocuments(
  tempFundOpportunityId: string,
  documents: UploadedDocument[],
): void {
  const storageKey = getUploadedDocumentsStorageKey(tempFundOpportunityId);
  localStorage.setItem(storageKey, JSON.stringify(documents));
}

export function buildModelResponseText(response: string, question: string): string {
  return `${response}\n\n${question}`.trim();
}

// The backend's first-turn `responseToUserQuestion` (and even its own
// opening `question`) is replaced entirely with this fixed greeting — the
// "how this works" messaging now lives in the page's own subheader instead.
export const WELCOME_MESSAGE_TEXT =
  "Hi, let's get started!\n\nLet me know the name of your new fund and share a short summary, or drag and drop a file for me to grab key info.";

export function buildWelcomeMessageText(): string {
  return WELCOME_MESSAGE_TEXT;
}

export function appendChatResponseMessage(
  messages: AIChatMessage[],
  result: FundOpportunityChatResponse,
): AIChatMessage[] {
  const { furtherRequestForDetail } = result;
  const question = furtherRequestForDetail?.question || '';
  const response = furtherRequestForDetail?.responseToUserQuestion || '';

  if (!question && !response) {
    return messages;
  }

  const isWelcomeTurn = messages.length === 0;
  const text = isWelcomeTurn
    ? buildWelcomeMessageText()
    : buildModelResponseText(response, question);

  return [
    ...messages,
    {
      role: 'model',
      parts: [{ text }],
      timestamp: new Date(),
    },
  ];
}

export function isWordDocument(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return lower.endsWith('.docx') || lower.endsWith('.doc');
}

export function hasWordDocumentsInUploads(
  uploadProgress: Map<string, UploadProgress>,
  uploadedDocuments: UploadedDocument[],
): boolean {
  const progressHasWord = Array.from(uploadProgress.values()).some((progress) =>
    isWordDocument(progress.fileName),
  );
  const uploadedHasWord = uploadedDocuments.some((document) => isWordDocument(document.name));
  return progressHasWord || uploadedHasWord;
}

export function countCompletedUploads(
  uploadProgress: Map<string, UploadProgress>,
  uploadedDocuments: UploadedDocument[],
): number {
  const completedInProgress = Array.from(uploadProgress.values()).filter(
    (progress) => progress.status === 'completed',
  ).length;
  return completedInProgress + uploadedDocuments.length;
}

export function hasActiveUploadOperations(uploadProgress: Map<string, UploadProgress>): boolean {
  return Array.from(uploadProgress.values()).some(
    (progress) =>
      progress.status === 'uploading' ||
      progress.status === 'processing' ||
      progress.status === 'pending',
  );
}

export function shouldShowProcessDocumentsButton(
  uploadProgress: Map<string, UploadProgress>,
  uploadedDocuments: UploadedDocument[],
  isProcessing: boolean,
): boolean {
  const hasCompletedDocuments =
    Array.from(uploadProgress.values()).some((progress) => progress.status === 'completed') ||
    uploadedDocuments.length > 0;

  return hasCompletedDocuments && !hasActiveUploadOperations(uploadProgress) && !isProcessing;
}

export function summarizeValidationIssues(
  details: Array<{ path?: Array<string | number>; message?: string }>,
): string {
  return details
    .map((issue) => {
      const field = issue.path?.[0];
      const label = field ? FundOpportunityLabels[String(field)] || String(field) : 'a field';
      return issue.message ? `${label} (${issue.message})` : label;
    })
    .join(', ');
}

export function removeAddressesAtIndices<T>(addresses: T[], addressIndices: number[]): T[] {
  const sortedIndices = [...addressIndices].sort((a, b) => b - a);
  const updatedAddresses = [...addresses];
  sortedIndices.forEach((index) => {
    updatedAddresses.splice(index, 1);
  });
  return updatedAddresses;
}
